import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { getAccessToken } from '@/api/client'
import {
  getGeneration,
  getGenerationTasks,
  type CreateGenerationRequest,
  type CreateGenerationResponse,
  type GenerationTaskSummary,
} from '@/api/generation'
import { setPoints, useAppStore } from '@/hooks/useAppStore'
import { pushNotification } from '@/components/feature/notificationBus'
import FirstGenModal from '@/pages/generate/components/FirstGenModal'
import {
  clearLocalPending,
  clearUnresolvedLocalPendingsForDurableTasks,
} from '@/pages/generate/utils/pendingGeneration'
import {
  GenerationTaskContext,
  mergeGenerationTasks,
  shouldShowGenerationCapacity,
  taskSummaryFromCreateAck,
} from '@/contexts/generationTaskStore'

export function GenerationTaskProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { user } = useAppStore()
  const userId = user?.userId
  const [tasksById, setTasksById] = useState<Record<string, GenerationTaskSummary>>({})
  const tasksByIdRef = useRef<Record<string, GenerationTaskSummary>>({})
  const [activeCount, setActiveCount] = useState(0)
  const [activeLimit, setActiveLimit] = useState(0)
  const [capacityReady, setCapacityReady] = useState(false)
  const initialized = useRef(false)
  const lastUpdatedAt = useRef<string | undefined>(undefined)
  const previousUserId = useRef<string | null>(null)
  const currentUserId = useRef<string | null>(userId ?? null)
  const refreshInFlight = useRef(false)
  const shownModalOffers = useRef(new Set<string>())
  const terminalEffects = useRef(new Set<string>())
  const balanceVersion = useRef(0)
  const [modalOffer, setModalOffer] = useState<{
    generationId: string
    contextId: string
  } | null>(null)

  const applyCapacity = useCallback((nextActiveCount: number, nextActiveLimit: number) => {
    setActiveCount(Math.max(0, nextActiveCount))
    setActiveLimit(Math.max(0, nextActiveLimit))
    setCapacityReady(true)
  }, [])

  const acceptCreateAck = useCallback((
    created: CreateGenerationResponse,
    payload: CreateGenerationRequest,
  ) => {
    if (currentUserId.current !== userId) return
    const summary = taskSummaryFromCreateAck(created, payload)
    setTasksById((current) => {
      const { next } = mergeGenerationTasks(current, [summary])
      tasksByIdRef.current = next
      return next
    })
    if (created.active_count != null && created.active_limit != null) {
      applyCapacity(created.active_count, created.active_limit)
    }
    if (
      created.points_balance_after != null
      && created.balance_version != null
      && created.balance_version > balanceVersion.current
    ) {
      balanceVersion.current = created.balance_version
      setPoints(created.points_balance_after)
    }
  }, [applyCapacity, userId])

  useEffect(() => {
    const nextUserId = user?.userId ?? null
    currentUserId.current = nextUserId
    if (previousUserId.current !== nextUserId) {
      setTasksById({})
      tasksByIdRef.current = {}
      setActiveCount(0)
      setActiveLimit(0)
      setCapacityReady(false)
      lastUpdatedAt.current = undefined
      initialized.current = false
      shownModalOffers.current.clear()
      terminalEffects.current.clear()
      balanceVersion.current = 0
      setModalOffer(null)
      previousUserId.current = nextUserId
    }
  }, [user?.userId])

  const refresh = useCallback(async () => {
    if (!getAccessToken() || !userId || refreshInFlight.current) return
    refreshInFlight.current = true
    const requestedUserId = userId
    try {
      const [active, recent] = await Promise.all([
        getGenerationTasks('active'),
        getGenerationTasks('recent', lastUpdatedAt.current),
      ])
      if (currentUserId.current !== requestedUserId) return
      applyCapacity(active.active_count, active.active_limit)
      const balanceProjection = active.balance_version >= recent.balance_version ? active : recent
      if (balanceProjection.balance_version > balanceVersion.current) {
        balanceVersion.current = balanceProjection.balance_version
        setPoints(balanceProjection.points_balance)
      }
      const incoming = [...active.items, ...recent.items]
      clearUnresolvedLocalPendingsForDurableTasks(requestedUserId, incoming)
      for (const task of incoming) {
        if (['success', 'failed', 'blocked', 'timeout'].includes(task.status)) {
          clearLocalPending(task.gen_id)
        }
      }
      const projectionConflicts = mergeGenerationTasks(tasksByIdRef.current, incoming).conflicts
      const uniqueConflicts = Array.from(
        new Map(projectionConflicts.map((item) => [item.gen_id, item])).values(),
      )
      for (const conflict of uniqueConflicts) {
        void getGeneration(conflict.gen_id).then((authoritative) => {
          setTasksById((current) => {
            const existing = current[conflict.gen_id] ?? conflict
            const reconciled = {
              ...existing,
              status: authoritative.status,
              status_version: authoritative.status_version ?? existing.status_version,
              status_updated_at: authoritative.status_updated_at ?? existing.status_updated_at,
              points_state: authoritative.points_state ?? existing.points_state,
              reason_code: authoritative.reason_code,
              failure_stage: authoritative.failure_stage,
              retry_action: authoritative.retry_action ?? existing.retry_action,
            }
            if (reconciled.status_version < existing.status_version) return current
            if (
              ['success', 'failed', 'blocked', 'timeout'].includes(existing.status)
              && reconciled.status !== existing.status
            ) {
              console.error('[generation-tasks] authoritative terminal conflict', {
                gen_id: existing.gen_id,
                stored_status: existing.status,
                authoritative_status: reconciled.status,
              })
              return current
            }
            const next = { ...current, [conflict.gen_id]: reconciled }
            tasksByIdRef.current = next
            return next
          })
        }).catch((error) => {
          console.warn('[generation-tasks] conflict reconciliation failed', error)
        })
      }
      const firstModal = incoming.find((task) => (
        task.membership_offer?.surface === 'modal'
        && ['eligible', 'impressed'].includes(task.membership_offer.state)
        && !shownModalOffers.current.has(task.membership_offer.context_id)
      ))
      if (firstModal?.membership_offer) {
        shownModalOffers.current.add(firstModal.membership_offer.context_id)
        setModalOffer({
          generationId: firstModal.gen_id,
          contextId: firstModal.membership_offer.context_id,
        })
      }
      setTasksById((current) => {
        const { next, completed, conflicts } = mergeGenerationTasks(current, incoming)
        if (conflicts.length > 0) {
          console.warn('[generation-tasks] same-version status conflict', conflicts.map((item) => ({
            gen_id: item.gen_id,
            status: item.status,
            status_version: item.status_version,
          })))
        }
        for (const task of incoming) {
          if (!lastUpdatedAt.current || task.status_updated_at > lastUpdatedAt.current) {
            lastUpdatedAt.current = task.status_updated_at
          }
        }
        if (initialized.current) {
          for (const task of completed) {
            const effectKey = `${task.gen_id}:${task.status_version}`
            if (terminalEffects.current.has(effectKey)) continue
            terminalEffects.current.add(effectKey)
            clearLocalPending(task.gen_id)
            if (task.status === 'success') {
              pushNotification('图片生成完成', '结果已保存到生图任务和作品记录', 'success')
            } else {
              pushNotification(
                '图片生成未完成',
                task.points_state === 'refunded' ? '积分已自动退回，可在任务中心查看原因' : '请在任务中心查看处理状态',
                'warn',
              )
            }
            window.dispatchEvent(new CustomEvent('generation:completed', { detail: task }))
          }
        }
        tasksByIdRef.current = next
        return next
      })
      initialized.current = true
    } catch (error) {
      console.warn('[generation-tasks] refresh failed', error)
    } finally {
      refreshInFlight.current = false
    }
  }, [applyCapacity, userId])

  useEffect(() => {
    void refresh()
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void refresh()
    }, 4000)
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refresh()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [refresh])

  const tasks = useMemo(
    () => (Object.values(tasksById) as GenerationTaskSummary[])
      .sort((a, b) => b.created_at.localeCompare(a.created_at)),
    [tasksById],
  )

  const value = useMemo(
    () => ({
      tasks,
      activeCount,
      activeLimit,
      capacityReady,
      refresh,
      acceptCreateAck,
      applyCapacity,
    }),
    [tasks, activeCount, activeLimit, capacityReady, refresh, acceptCreateAck, applyCapacity],
  )

  return (
    <GenerationTaskContext.Provider value={value}>
      {children}
      {shouldShowGenerationCapacity(capacityReady, activeCount, activeLimit) ? (
        <button
          type="button"
          aria-label={`generation-capacity-${activeCount}-${activeLimit}`}
          data-testid="generation-capacity"
          onClick={() => navigate('/profile?tab=history')}
          className="fixed right-4 z-40 flex items-center gap-2 rounded-full bg-gray-900 px-4 py-2.5 text-sm font-semibold text-white shadow-xl"
          style={{ bottom: 'calc(5.5rem + env(safe-area-inset-bottom))' }}
        >
          <i className={activeCount > 0 ? 'ri-loader-4-line animate-spin' : 'ri-stack-line'} />
          {t('gen_capacityIndicator', {
            active: activeCount,
            limit: activeLimit,
            defaultValue: '{{active}}/{{limit}} 任务',
          })}
        </button>
      ) : null}
      <FirstGenModal
        isOpen={modalOffer !== null}
        onClose={() => setModalOffer(null)}
        generationId={modalOffer?.generationId}
        offerContextId={modalOffer?.contextId}
      />
    </GenerationTaskContext.Provider>
  )
}
